import { Volume2, VolumeX } from 'lucide-react';
import { PrayerDisplay } from '../types/prayer';

interface PrayerTableProps {
  prayers: PrayerDisplay[];
  nextPrayer: string;
  mutedPrayers: Set<string>;
  onToggleMute: (key: string) => void;
  timeUntilNext?: string;
}

export function PrayerTable({ prayers, nextPrayer, mutedPrayers, onToggleMute, timeUntilNext }: PrayerTableProps) {
  const prayerEmojis: Record<string, string> = {
    fajr: '🌅',
    sunrise: '☀️',
    zuhr: '🌞',
    asr: '🌤️',
    maghrib: '🌆',
    isha: '🌙'
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden border border-emerald-100">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-600 text-white">
              <th className="px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 text-left text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-wide" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }}>Prayer</th>
              <th className="px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 text-left text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-wide" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }}>الصلاة</th>
              <th className="px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 text-left text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-wide" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }}>Time</th>
              <th className="px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 text-center text-sm sm:text-base md:text-lg lg:text-xl font-bold tracking-wide" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }}>Sound</th>
            </tr>
          </thead>
          <tbody>
            {prayers.map((prayer, index) => {
              const isNext = prayer.key === nextPrayer;
              const isMuted = mutedPrayers.has(prayer.key);

              return (
                <tr
                  key={prayer.key}
                  className={`border-b border-gray-100 transition-all duration-300 ${
                    isNext
                      ? 'bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 ring-2 ring-inset ring-emerald-400 shadow-lg'
                      : index % 2 === 0
                      ? 'bg-gray-50/50 hover:bg-emerald-50/50'
                      : 'bg-white hover:bg-emerald-50/30'
                  }`}
                >
                  <td className={`px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-6 ${isNext ? 'text-emerald-700' : 'text-gray-800'}`}>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <span className="text-xl sm:text-2xl md:text-3xl flex-shrink-0" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>{prayerEmojis[prayer.key]}</span>
                      <div className="min-w-0">
                        <div className="text-sm sm:text-base md:text-lg lg:text-xl font-bold" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.3))' }}>{prayer.name}</div>
                        {isNext && (
                          <div className="flex items-center gap-1 sm:gap-2 mt-1 flex-wrap">
                            <span className="inline-flex items-center bg-emerald-500 text-white text-xs px-2 py-0.5 sm:px-3 sm:py-1 rounded-full font-semibold shadow-sm whitespace-nowrap" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))' }}>
                              ⭐ Next
                            </span>
                            {timeUntilNext && (
                              <span className="inline-flex items-center bg-emerald-100 text-emerald-700 text-xs sm:text-sm px-2 py-0.5 sm:px-3 sm:py-1.5 rounded-full font-bold shadow-sm whitespace-nowrap" style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.2))' }}>
                                ⏰ {timeUntilNext}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={`px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-6 text-xl sm:text-2xl md:text-3xl font-bold ${isNext ? 'text-emerald-600' : 'text-gray-700'}`} style={{ filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.3))' }}>
                    {prayer.nameArabic}
                  </td>
                  <td className={`px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-6 text-base sm:text-lg md:text-xl lg:text-2xl font-bold ${isNext ? 'text-emerald-700' : 'text-gray-900'}`} style={{ filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.3))' }}>
                    {prayer.time}
                  </td>
                  <td className="px-3 py-3 sm:px-6 sm:py-4 md:px-8 md:py-6 text-center">
                    <button
                      onClick={() => onToggleMute(prayer.key)}
                      className={`p-2 sm:p-2.5 md:p-3 rounded-lg sm:rounded-xl transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-110 ${
                        isMuted
                          ? 'bg-red-100 text-red-600 hover:bg-red-200'
                          : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                      }`}
                      title={isMuted ? 'Unmute' : 'Mute'}
                    >
                      {isMuted ? <VolumeX className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" /> : <Volume2 className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
